import {Controller, FieldError, Path, RegisterOptions, useFormContext, FieldValues} from "react-hook-form";
import {DropdownMultiSelect} from "common";
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from "../../../placement/const";

type ModalMultiSelectionInputProps<T extends FieldValues> = {
    options: string[];
    label: string;
    disabled: boolean;
    required?: boolean;
    field: keyof T;
    labelMapperFunc?: (value: string) => string;
    optionalRules?: RegisterOptions;
    placeholder?: string;
    labelPosition?: 'left' | 'top';
    width?: string;
    labelClassName?: string;
    /** Chosen values as chips with an x, instead of a joined string in the closed input. */
    showBadges?: boolean;
    /** Colour and shape of a chip; the picker's own blue pill when left out. */
    badgeClassName?: string;
};

export function ModalMultiSelectionInput<T extends FieldValues>({
                                               label,
                                               disabled,
                                               field,
                                               optionalRules,
                                               options,
                                               required = true,
                                               labelMapperFunc,
                                               placeholder = '-  Please choose',
                                               labelPosition = 'left',
                                               width = 'w-full',
                                               labelClassName,
                                               showBadges = false,
                                               badgeClassName,
                                           }: ModalMultiSelectionInputProps<T>) {
    const {
        control,
        formState: { errors },
        setValue,
        clearErrors,
    } = useFormContext<T>();
    const fieldErrors = errors as Record<keyof T, FieldError>;
    
    let rules: RegisterOptions = {required: {value: required, message: "Required"}};
    if (optionalRules) {
        rules = optionalRules;
    }

    const isLabelTop = labelPosition === 'top';

    const renderDropdown = () => (
        <Controller
            name={field as string as Path<T>}
            control={control as any}
            rules={rules}
            render={({field: {value}}) => {
                const joined = Array.isArray(value) ? value.join(SPLIT_DELIMITER) : (value || '');
                return (
                    <DropdownMultiSelect
                        width={width}
                        field={field as string}
                        placeholder={placeholder}
                        options={options}
                        labelMapperFunc={labelMapperFunc}
                        value={joined}
                        setValue={(values) => {
                            setValue(field as Path<T>, splitAndRemoveEmpty(values) as any);
                            clearErrors(field as Path<T>);
                        }}
                        hasError={fieldErrors[field] !== undefined}
                        disabled={disabled}
                        showBadges={showBadges}
                        badgeClassName={badgeClassName}
                        onRemoveBadge={showBadges ? (removed) => {
                            const kept = splitAndRemoveEmpty(joined).filter((v) => v !== removed);
                            setValue(field as Path<T>, kept as any);
                            clearErrors(field as Path<T>);
                        } : undefined}
                    />
                );
            }}
        />
    );

    const renderLabel = () => (
        <>
            {label}
        </>
    );

    return (
        <div className="w-full text-black-200 py-2 text-base items-center">
            {isLabelTop ? (
                // Top label layout
                <div className="w-full">
                    <div className={`mb-2 ${labelClassName ?? ""}`}>
                        {renderLabel()}
                    </div>
                    {renderDropdown()}
                </div>
            ) : (
                // Left label layout
                <div className="flex items-center">
                    <div className={`w-2/6 text-right ${labelClassName ?? ""}`}>
                        {renderLabel()}
                    </div>
                    <div className="w-4/6 ml-6">
                        {renderDropdown()}
                    </div>
                </div>
            )}
            {
                fieldErrors[field] &&
                <div className={`flex ${!isLabelTop ? 'w-full pt-2' : ''}`}>
                    {!isLabelTop && <div className="w-2/6"></div>}
                    <div className={`${!isLabelTop ? 'w-4/6 ml-6' : 'w-full'} text-red-700`}>
                        {fieldErrors[field].message}
                    </div>
                </div>
            }
        </div>
    );
}
